import React, { useMemo, useState } from "react";

const toText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.join(" ");
  return "";
};

const DataTable = ({ columns, rows }) => {
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const needle = search.toLowerCase();
    return rows.filter((row) =>
      columns.some((col) => {
        if (col.searchable === false) return false;
        const value = typeof col.accessor === "function" ? col.accessor(row) : row[col.accessor];
        return toText(value).toLowerCase().includes(needle);
      })
    );
  }, [columns, rows, search]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    const column = columns.find((item) => item.key === sortKey);
    if (!column) return filteredRows;
    const sorted = [...filteredRows];
    sorted.sort((a, b) => {
      const aSource =
        typeof column.sortAccessor === "function"
          ? column.sortAccessor(a)
          : typeof column.accessor === "function"
            ? column.accessor(a)
            : a[column.accessor];
      const bSource =
        typeof column.sortAccessor === "function"
          ? column.sortAccessor(b)
          : typeof column.accessor === "function"
            ? column.accessor(b)
            : b[column.accessor];
      const aText = toText(aSource).toLowerCase();
      const bText = toText(bSource).toLowerCase();
      if (aText < bText) return sortDirection === "asc" ? -1 : 1;
      if (aText > bText) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [columns, filteredRows, sortDirection, sortKey]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);
  const pageRows = sortedRows.slice(startIndex, endIndex);

  const onSort = (column) => {
    if (column.sortable === false) return;
    if (sortKey === column.key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(column.key);
    setSortDirection("asc");
  };

  const movePage = (nextPage) => {
    setCurrentPage(Math.max(1, Math.min(nextPage, totalPages)));
  };

  return (
    <section className="table-card">
      <div className="table-controls">
        <label>
          Show{" "}
          <select
            value={pageSize}
            onChange={(event) => {
              setPageSize(Number(event.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>{" "}
          entries
        </label>
        <label>
          Search:
          <input
            type="text"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setCurrentPage(1);
            }}
          />
        </label>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} onClick={() => onSort(column)}>
                  <span className={column.sortable === false ? "th-label" : "th-label sortable"}>
                    {column.label}
                    <span className="sort-arrows">
                      {sortKey === column.key ? (sortDirection === "asc" ? "▲" : "▼") : "↕"}
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="empty-cell">
                  No entries found.
                </td>
              </tr>
            )}
            {pageRows.map((row, rowIndex) => (
              <tr key={row._id || row.id || `${startIndex}-${rowIndex}`}>
                {columns.map((column) => {
                  const value =
                    typeof column.accessor === "function" ? column.accessor(row) : row[column.accessor];
                  return (
                    <td key={column.key}>
                      {typeof column.render === "function"
                        ? column.render(row, startIndex + rowIndex + 1)
                        : value}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-pagination">
        <span>
          Showing {total === 0 ? 0 : startIndex + 1} to {endIndex} of {total} entries
        </span>
        <div className="page-controls">
          <button type="button" onClick={() => movePage(safePage - 1)} disabled={safePage === 1}>
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, index) => index + 1)
            .slice(0, 7)
            .map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                className={pageNumber === safePage ? "active-page" : ""}
                onClick={() => movePage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
          <button type="button" onClick={() => movePage(safePage + 1)} disabled={safePage === totalPages}>
            Next
          </button>
        </div>
      </div>
    </section>
  );
};

export default DataTable;
